import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import "./registerFonts";
import {
	getInitials,
	getInitialsColor,
	parseCoverLetterParts,
	formatLetterDate
} from "./coverLetterPdfUtils";

interface CoverLetterDocumentProps {
	fullName: string;
	email: string;
	phone: string;
	city: string;
	state: string;
	coverLetterText: string;
	letterDate: Date;
}

const styles = StyleSheet.create({
	page: {
		paddingTop: 3.6, // 0.05"
		paddingBottom: 3.6,
		paddingLeft: 54, // 0.75"
		paddingRight: 54,
		fontFamily: "Varela Round",
		fontSize: 11
	},
	letterhead: {
		alignItems: "center",
		marginBottom: 8
	},
	initials: {
		fontFamily: "Lavishly Yours",
		fontSize: 42,
		textAlign: "center"
	},
	fullName: {
		fontFamily: "Libre Caslon Text",
		fontSize: 14,
		textTransform: "uppercase",
		letterSpacing: 6,
		color: "#000000",
		marginTop: -43,
		textAlign: "center"
	},
	body: {
		marginTop: 20
	},
	greetingRow: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-start",
		marginBottom: 8
	},
	greetingText: {
		lineHeight: 1.3,
		color: "#000000"
	},
	dateText: {
		lineHeight: 1.3,
		color: "#000000",
		textAlign: "right"
	},
	paragraph: {
		marginBottom: 8,
		lineHeight: 1.3,
		color: "#000000"
	},
	closingLine: {
		marginTop: 12,
		lineHeight: 1.3,
		color: "#000000"
	},
	signature: {
		fontFamily: "Great Vibes",
		fontSize: 20,
		color: "#1a3a5c",
		marginTop: -26,
		transform: "rotate(-3deg)"
	},
	typedName: {
		marginTop: 12,
		lineHeight: 1.3,
		color: "#000000",
		transform: "rotate(0deg)"
	},
	footer: {
		position: "absolute",
		bottom: 14,
		left: 54,
		right: 54,
		textAlign: "center",
		fontFamily: "Varela Round",
		fontSize: 9,
		color: "#4a4a4a"
	}
});

const CoverLetterDocument = ({
	fullName,
	email,
	phone,
	city,
	state,
	coverLetterText,
	letterDate
}: CoverLetterDocumentProps) => {
	const initials = getInitials(fullName);
	const initialsColor = getInitialsColor(fullName);
	const { body, closingLine } = parseCoverLetterParts(coverLetterText);
	const dateStr = formatLetterDate(letterDate);

	const paragraphs = body.split(/\n\s*\n/).filter((p) => p.trim().length > 0);

	const formatPhone = (raw: string) => {
		const digits = raw.replace(/\D/g, "");
		if (digits.length === 10) {
			return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
		}
		return raw;
	};

	const footerParts: string[] = [];
	if (phone) footerParts.push(formatPhone(phone));
	if (email) footerParts.push(email);
	if (city && state) footerParts.push(`${city}, ${state}`);
	else if (city) footerParts.push(city);
	else if (state) footerParts.push(state);
	const footerText = footerParts.join(" \u00B7 ");

	return (
		<Document>
			<Page size="LETTER" style={styles.page}>
				{/* Letterhead — initials with name overlapping */}
				<View style={styles.letterhead}>
					{initials.length > 0 && (
						<Text style={[styles.initials, { color: initialsColor }]}>
							{initials}
						</Text>
					)}
					<Text style={styles.fullName}>{fullName}</Text>
				</View>

				{/* Body */}
				<View style={styles.body}>
					{/* Greeting and date on same row, top-aligned */}
					<View style={styles.greetingRow}>
						{paragraphs.length > 0 && (
							<Text style={styles.greetingText}>{paragraphs[0].trim()}</Text>
						)}
						<Text style={styles.dateText}>{dateStr}</Text>
					</View>

					{paragraphs.slice(1).map((para, i) => (
						<Text key={i} style={styles.paragraph}>
							{para.trim()}
						</Text>
					))}

					{closingLine.length > 0 && (
						<Text style={styles.closingLine}>{closingLine}</Text>
					)}

					<Text style={styles.signature}>
						{fullName
							.split(/\s+/)
							.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
							.join(" ")}
					</Text>

					<Text style={styles.typedName}>{fullName}</Text>
				</View>

				{/* Footer */}
				{footerText.length > 0 && (
					<Text style={styles.footer}>{footerText}</Text>
				)}
			</Page>
		</Document>
	);
};

export default CoverLetterDocument;
